/* eslint-disable react/no-array-index-key */
/* eslint-disable max-lines */
/* eslint-disable react/no-unknown-property */
/* eslint-disable @scandipwa/scandipwa-guidelines/only-render-in-component */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import Field from 'Component/Field';
import FIELD_TYPE from 'Component/Field/Field.config';
import TextPlaceholder from 'Component/TextPlaceholder';
import { LocationType } from 'Type/Router.type';
import { getQueryParam } from 'Util/Url';

import { CATEGORY_SORT, DESKTOP_MIN_WIDTH } from './CategorySort.config';

import './CategorySort.extended.style.scss';

/**
 * Category sort
 * @class CategorySort
 * @namespace Scandipwa/Component/CategorySort/Component */
export class CategorySortComponent extends PureComponent {
    static propTypes = {
        onSortChange: PropTypes.func.isRequired,
        sortKey: PropTypes.string.isRequired,
        sortDirection: PropTypes.string.isRequired,
        selectOptions: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number
            ]),
            value: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number
            ]),
            disabled: PropTypes.bool,
            label: PropTypes.string
        })).isRequired,
        config: PropTypes.arrayOf(PropTypes.string).isRequired,
        location: LocationType.isRequired,
        // eslint-disable-next-line react/boolean-prop-naming
        areComponentsLoading: PropTypes.bool.isRequired
    };

    state = {
        close: false,
        isDesktop: false,
        checkedDesktopSize: false
    };

    renderSortButton = this.renderSortButton.bind(this);

    setDefaultSort = this.setDefaultSort.bind(this);

    updateWindowDimensions = this.updateWindowDimensions.bind(this);

    /**
     * On component did mount
     */
    componentDidMount() {
        this.updateWindowDimensions();
        window.addEventListener('resize', this.updateWindowDimensions);
    }

    /**
     * On component will unmout
     */
    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions);
    }

    getBanner = () => {
        const bannerList = document.getElementById('global-promo-banner-list');
        const bannerItem = document.querySelector('[id^=\'global_promo_banner_\']');
        return bannerList || bannerItem;
    };

    onChange = (value, isMobileSort = false) => {
        const { onSortChange } = this.props;
        const selectedValue = isMobileSort ? value.target.value : value;

        const [direction, ...key] = selectedValue.split(' ');

        onSortChange(direction, key);

        if (this.getBanner()) {
            document.documentElement.classList.add('sticky-ios');
        }
    };

    renderPlaceholder() {
        return (
            <p block="CategorySort" elem="Placeholder">
                <TextPlaceholder content={ false } length="short" />
            </p>
        );
    }

    /**
     * Updates window dimensions
     */
    updateWindowDimensions() {
        const { isDesktop } = this.state;
        const newIsDesktop = (window.innerWidth >= DESKTOP_MIN_WIDTH);

        if (isDesktop !== newIsDesktop) {
            this.setState({ isDesktop: newIsDesktop });
        }

        this.setState({ checkedDesktopSize: true });
    }

    /**
     * Checks if filter is applied from url
     *
     * @returns {boolean}
     */
    isFilterAppliedFromUrl() {
        const { location } = this.props;

        const filterAppliedFromUrl = getQueryParam('sortKey', location);

        return !!filterAppliedFromUrl;
    }

    /**
     * Sets default sort
     */
    setDefaultSort() {
        const { onSortChange, config } = this.props;
        const [sortKey, sortDirection] = config;

        this.setState({ close: true });
        onSortChange(sortDirection, sortKey);
    }

    /**
     * Render sort button
     *
     * @returns {JSX.Element}
     */
    renderSortButton(option, index) {
        const {
            sortKey: selectedSortKey,
            sortDirection: selectedSortDirection
        } = this.props;
        const { close } = this.state;
        const { label, id } = option;

        const [sortDirection, sortKey] = id.split(' ');
        const isSelected = selectedSortKey === sortKey && selectedSortDirection === sortDirection && !close;

        return (
            <div
              key={ index }
              block="CategorySort"
              elem="SortButtonWrapper"
            >
                <button
                  block="CategorySort"
                  elem="SortButton"
                  mods={ { isSelected } }
                  key={ id }
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={ () => {
                      this.setState({ close: false });
                      this.onChange(`${sortDirection} ${sortKey}`);
                  } }
                >
                    { label }
                </button>
                { /* eslint-disable-next-line jsx-a11y/control-has-associated-label */ }
                <button
                  block="CategorySort"
                  elem="RemoveSort"
                  mods={ { isSelected } }
                  onClick={ this.setDefaultSort }
                />
            </div>
        );
    }

    /**
     * Render sort title
     *
     * @returns {JSX.Element}
     */
    renderSortTitle() {
        return (
          <span
            block="CategorySort"
            elem="Title"
            mods={ { mobileTitleVisible: !this.isFilterAppliedFromUrl() } }
          >
              { __('Sort By:') }
          </span>
        );
    }

    /**
     * Renders place holders
     * @returns {JSX.Element|unknown[]}
     */
    renderPlaceholders() {
        const { isDesktop } = this.state;

        if (!isDesktop) {
            return null;
        }

        return (
            <p
              block="CategorySort"
              elem="Placeholder"
            >
                <TextPlaceholder content={ false } length="short" />
            </p>
        );
    }

    /**
     * Renders sort field
     *
     * @returns {JSX.Element|*}
     */
    renderSortField() {
        const {
            sortKey,
            sortDirection,
            selectOptions,
            areComponentsLoading
        } = this.props;
        const { isDesktop, checkedDesktopSize } = this.state;

        const sortedSelectOptions = this.moveDefaultAtBeggining(selectOptions);

        if (areComponentsLoading || !checkedDesktopSize) {
            return this.renderPlaceholders();
        }

        if (isDesktop) {
            const sanitizedSortedSelectOptions = sortedSelectOptions.map((option) => ({
                ...option,
                id: option.id.replace(' ', '_')
            }));

            return (
                <Field
                  type={ FIELD_TYPE.select }
                  mix={ { block: 'CategorySort', elem: 'Select' } }
                  attr={ {
                      id: 'category-sort',
                      name: 'category-sort',
                      selectPlaceholder: __('Sort By'),
                      value: this.isFilterAppliedFromUrl() ? `${sortDirection} ${sortKey}` : ''
                  } }
                  events={ {
                      onChange: this.onChange,
                      onFocus: false
                  } }
                  options={ sanitizedSortedSelectOptions }
                />
            );
        }

        sortedSelectOptions.unshift({
            id: 'sort-by',
            label: __('Sort By:'),
            value: '',
            disabled: true,
            selected: true
        });

        return (
            <div block="CategorySort" elem="SelectWrapper">
                <select
                  id="category-sort"
                  name={ CATEGORY_SORT }
                  value={ this.isFilterAppliedFromUrl() ? `${sortDirection} ${sortKey}` : '' }
                  // eslint-disable-next-line react/jsx-no-bind
                  onChange={ (e) => this.onChange(e, true) }
                  block="CategorySort"
                  elem="Select"
                >
                    { sortedSelectOptions.map(({ label, value }, index) => (
                        <option key={ index } value={ value }>{ label }</option>
                    )) }
                </select>
            </div>
        );
    }

    moveDefaultAtBeggining(sorts) {
        const defaultLabel = 'Default sort';

        return sorts.sort(
            // eslint-disable-next-line no-nested-ternary
            (x, y) => (x.label === defaultLabel ? -1 : y.label === defaultLabel ? 1 : 0)
        );
    }

    render() {
        const { areComponentsLoading } = this.props;

        return (
            <div block="CategorySort" mods={ { isPlaceHolder: areComponentsLoading } }>
                { this.renderSortField() }
            </div>
        );
    }
}

export default CategorySortComponent;
